package com.eladfinish.bible.contest.screens.search.usecases.date

import com.eladfinish.bible.contest.constants.StringConstants.DATE_SYMBOL

/**
 * Parses the text of a 📅 search into a [DateQuery] — any reasonable Hebrew / biblical way of
 * writing a day and a month, including date phrases pasted straight from the Bible (with nikud
 * and te'amim, and with the year around them):
 *
 * "10 בתשרי", "10 לחודש ה-7", "עשירי לשביעי", "י׳ תשרי", "10/7", "תשרי 10",
 * "בַּחֹדֶשׁ הַשְּׁבִיעִי בֶּעָשׂוֹר לַחֹדֶשׁ", "בשנה השנית בחדש השני בעשרים בחדש",
 * "בשלושה עשר לחדש שנים עשר הוא חדש אדר", "ניסן" (the whole month).
 *
 * Input that can't be read as exactly one date fails with a [DateQueryError] instead of being
 * guessed into a wrong date.
 *
 * How day and month are told apart:
 * 1. a number right after "חדש"/"חודש"/"ירח" (not itself prefixed by ב/ל), or right before a bare
 *    "חדש" ("בעשתי עשר חדש") is the month;
 * 2. a number before "לחדש"/"בחדש" that isn't followed by a month is the day ("באחד לחדש");
 * 3. numbers next to "שנה"/"שנת"/"שנים" are the year, and are ignored;
 * 4. month names ("תשרי", "איתנים", "אדר ב׳") and "ראש חודש" (= day 1) speak for themselves;
 * 5. of two remaining numbers the first is the day and the second the month ("10 7", "עשירי לשביעי"),
 *    unless exactly one of them is an ordinal — then that one is the month ("בשביעי בעשרים ואחד");
 * 6. a single remaining ordinal is a month ("בעשירי"), a single remaining cardinal is a day.
 *
 * No regex on purpose: `\s` and `\b` behave differently on Android, iOS/Native, JVM and JS for NBSP
 * and Hebrew letters.
 */
class ParseDateQueryUseCase {

    operator fun invoke(query: String): ParseDateQueryResult {
        val words = tokenize(normalize(query))
        if (words.isEmpty()) return failure(DateQueryError.EMPTY)

        val items = toItems(words)
        return resolve(items)
    }

    // region normalization

    private fun normalize(query: String): String {
        val text = query.replace(DATE_SYMBOL, " ").replace(VARIATION_SELECTOR, ' ')
        val out = StringBuilder(text.length)
        for ((index, ch) in text.withIndex()) {
            when {
                ch in 'א'..'ת' || ch.isAsciiDigit() -> out.append(ch)
                ch in GERESH_CHARS -> out.append(GERESH)
                // "ה-7", "ב־10": the prefix belongs to the number
                (ch == '-' || ch == MAKAF) && isHebrewLetter(text.getOrNull(index - 1)) &&
                    text.getOrNull(index + 1)?.isAsciiDigit() == true -> Unit

                ch in '֑'..'ׇ' && ch !in PUNCTUATION_MARKS -> Unit // nikud and te'amim
                else -> out.append(' ') // spaces (incl. NBSP), makaf, paseq, sof pasuk, / . , - …
            }
        }
        return out.toString()
    }

    private fun tokenize(text: String): List<Word> =
        text.split(' ')
            .filter { it.isNotEmpty() }
            .mapNotNull { raw ->
                val hasGeresh = GERESH in raw
                val plain = raw.replace(GERESH.toString(), "")
                if (plain.isEmpty()) null else Word(text = plain.withRegularFinals(), hasGeresh = hasGeresh)
            }

    // endregion

    // region lexing

    private fun toItems(words: List<Word>): List<Item> {
        val items = mutableListOf<Item>()
        var i = 0
        while (i < words.size) {
            val word = words[i]

            digitsNumber(word)?.let {
                items += it
                i++
                continue
            }

            if (word.hasGeresh) {
                gematriaNumber(word, allowPrefix = true)?.let {
                    items += it
                    i++
                    continue
                }
            }

            val match = lookup(word.text)
            val lexeme = match?.lexeme

            when {
                lexeme == Lexeme.Drop -> i++

                lexeme == Lexeme.Rosh -> {
                    if (lookup(words.getOrNull(i + 1)?.text)?.lexeme == Lexeme.Chodesh) {
                        items += Item.RoshChodesh
                        i += 2
                    } else {
                        items += Item.Other
                        i++
                    }
                }

                lexeme == Lexeme.RoshChodesh -> {
                    items += Item.RoshChodesh
                    i++
                }

                lexeme is Lexeme.Month -> {
                    items += Item.MonthName(lexeme.month)
                    i++
                    // "אדר א׳", "אדר שני": both are the 12th month
                    if (lexeme.month == ADAR && words.getOrNull(i)?.text in ADAR_SUFFIXES) i++
                }

                lexeme == Lexeme.Chodesh -> {
                    items += Item.Chodesh(prefixedByBetOrLamed = match.prefix.isBetOrLamed())
                    i++
                }

                lexeme is Lexeme.Year -> {
                    items += Item.Year(construct = lexeme.construct)
                    i++
                }

                else -> {
                    val number = cardinalNumber(words, i)
                        ?: ordinalNumber(match)
                        ?: gematriaNumber(word, allowPrefix = false)?.let { it to 1 }
                    if (number != null) {
                        items += reinterpretShnayimAsYear(items, number.first)
                        i += number.second
                    } else {
                        items += Item.Other
                        i++
                    }
                }
            }
        }
        return items
    }

    /** "שבע שנים": a bare "שנים" (two) right after a number is "years". */
    private fun reinterpretShnayimAsYear(items: List<Item>, number: Item.Number): Item =
        if (number.isBareShnayim && items.lastOrNull() is Item.Number) Item.Year(construct = false) else number

    private fun digitsNumber(word: Word): Item.Number? {
        val firstDigit = word.text.indexOfFirst { it.isAsciiDigit() }
        if (firstDigit < 0) return null
        val prefix = word.text.substring(0, firstDigit)
        val digits = word.text.substring(firstDigit)
        if (prefix.length > MAX_PREFIX_LENGTH || prefix.any { it !in PREFIX_LETTERS }) return null
        if (digits.any { !it.isAsciiDigit() } || digits.length > 4) return null
        return Item.Number(digits.toInt(), NumberKind.DIGITS, prefix)
    }

    private fun gematriaNumber(word: Word, allowPrefix: Boolean): Item.Number? {
        val text = word.text
        GEMATRIA[text]?.let { value ->
            // a lone ב/ל/ו/ה/מ without a geresh is a detached prefix, not a number
            if (!word.hasGeresh && text.length == 1 && text[0] in PREFIX_LETTERS) return null
            return Item.Number(value, NumberKind.GEMATRIA, prefix = "")
        }
        if (!allowPrefix) return null
        var i = 0
        while (i < MAX_PREFIX_LENGTH && i < text.length - 1 && text[i] in PREFIX_LETTERS) {
            i++
            GEMATRIA[text.substring(i)]?.let { value ->
                return Item.Number(value, NumberKind.GEMATRIA, prefix = text.substring(0, i))
            }
        }
        return null
    }

    private fun ordinalNumber(match: Match?): Pair<Item.Number, Int>? {
        val ordinal = match?.lexeme as? Lexeme.Ordinal ?: return null
        return Item.Number(ordinal.value, NumberKind.ORDINAL, match.prefix) to 1
    }

    /**
     * A cardinal number phrase starting at [start]: terms joined by ו —
     * "עשרים וארבעה", "האחד ועשרים", "שבעה עשר", "עשתי עשר", "באחת ושש מאות".
     * Returns the number and how many words it took.
     */
    private fun cardinalNumber(words: List<Word>, start: Int): Pair<Item.Number, Int>? {
        val first = cardinalTerm(words, start) ?: return null
        var value = first.value
        var index = start + first.length
        while (index < words.size) {
            val prefix = lookup(words[index].text)?.prefix ?: break
            if (prefix != "ו" && prefix != "וה") break
            val term = cardinalTerm(words, index) ?: break
            value += term.value
            index += term.length
        }
        val isBareShnayim = index - start == 1 && first.bareWord in SHNAYIM
        return Item.Number(
            value = value,
            kind = NumberKind.CARDINAL,
            prefix = first.prefix,
            isBareShnayim = isBareShnayim,
        ) to (index - start)
    }

    private class Term(val value: Int, val length: Int, val prefix: String, val bareWord: String)

    private fun cardinalTerm(words: List<Word>, index: Int): Term? {
        val match = lookup(words[index].text) ?: return null
        val next = words.getOrNull(index + 1)?.text
        val nextIsTen = next in TEEN_SUFFIXES
        val bare = match.bare

        TEEN_ONLY_UNITS[bare]?.let { unit ->
            return if (nextIsTen) Term(unit + 10, 2, match.prefix, bare) else null
        }

        return when (val lexeme = match.lexeme) {
            is Lexeme.Unit -> when {
                nextIsTen -> Term(lexeme.value + 10, 2, match.prefix, bare)
                next == HUNDREDS -> Term(lexeme.value * 100, 2, match.prefix, bare)
                else -> Term(lexeme.value, 1, match.prefix, bare)
            }

            is Lexeme.Ten -> Term(lexeme.value, 1, match.prefix, bare)
            else -> null
        }
    }

    private class Match(val prefix: String, val bare: String, val lexeme: Lexeme)

    /** The word as-is, or with up to three prefix letters (ו ב ל ה מ) removed. */
    private fun lookup(word: String?): Match? {
        if (word == null) return null
        LEXICON[word]?.let { return Match("", word, it) }
        var i = 0
        while (i < MAX_PREFIX_LENGTH && word.length - (i + 1) >= 2 && word[i] in PREFIX_LETTERS) {
            i++
            val bare = word.substring(i)
            LEXICON[bare]?.let { return Match(word.substring(0, i), bare, it) }
        }
        return null
    }

    // endregion

    // region resolving

    private fun resolve(items: List<Item>): ParseDateQueryResult {
        val roles = arrayOfNulls<Role>(items.size)
        val days = mutableListOf<Int>()
        val months = mutableListOf<Int>()

        fun number(index: Int): Item.Number? =
            (items.getOrNull(index) as? Item.Number)?.takeIf { roles[index] == null }

        items.forEach { item ->
            when (item) {
                is Item.MonthName -> months += item.month
                Item.RoshChodesh -> days += 1
                else -> Unit
            }
        }

        // 1a. "חדש השביעי", "לחדש 7", "לחדש שנים עשר": the number after is the month
        items.forEachIndexed { index, item ->
            if (item is Item.Chodesh) {
                val next = number(index + 1)
                if (next != null && !next.prefix.isBetOrLamed()) roles[index + 1] = Role.MONTH
            }
        }

        // 3. years: "בשנת שתים", "בשש מאות שנה", "בשנה השנית"
        items.forEachIndexed { index, item ->
            if (item !is Item.Year) return@forEachIndexed
            val before = number(index - 1)
            val after = number(index + 1)?.takeIf { !it.prefix.isBetOrLamed() }
            when {
                item.construct && after != null -> roles[index + 1] = Role.YEAR
                !item.construct && before != null -> roles[index - 1] = Role.YEAR
                !item.construct && after != null -> roles[index + 1] = Role.YEAR
            }
        }

        // 1b. "בעשתי עשר חדש" (month) / 2. "באחד לחדש", "בעשרים בחדש" (day)
        items.forEachIndexed { index, item ->
            if (item !is Item.Chodesh) return@forEachIndexed
            val next = items.getOrNull(index + 1)
            val monthFollows = next is Item.MonthName || (index + 1 < items.size && roles[index + 1] == Role.MONTH)
            if (monthFollows) return@forEachIndexed
            if (number(index - 1) != null) {
                roles[index - 1] = if (item.prefixedByBetOrLamed) Role.DAY else Role.MONTH
            }
        }

        val remaining = mutableListOf<Item.Number>()
        items.forEachIndexed { index, item ->
            if (item !is Item.Number) return@forEachIndexed
            when (roles[index]) {
                Role.DAY -> days += item.value
                Role.MONTH -> months += item.value
                Role.YEAR -> Unit
                null -> remaining += item
            }
        }

        val distinctDays = days.distinct()
        val distinctMonths = months.distinct()
        if (distinctDays.size > 1 || distinctMonths.size > 1) return failure(DateQueryError.AMBIGUOUS)
        var day = distinctDays.singleOrNull()
        var month = distinctMonths.singleOrNull()

        when {
            day != null && month != null -> {
                if (remaining.any { it.value != day && it.value != month }) {
                    return failure(DateQueryError.AMBIGUOUS)
                }
            }

            month != null -> {
                val values = remaining.map { it.value }.distinct()
                if (values.size > 1) return failure(DateQueryError.AMBIGUOUS)
                day = values.singleOrNull()
            }

            day != null -> {
                val values = remaining.map { it.value }.distinct()
                if (values.size > 1) return failure(DateQueryError.AMBIGUOUS)
                month = values.singleOrNull() ?: return failure(DateQueryError.MISSING_MONTH)
            }

            else -> when (remaining.size) {
                0 -> return failure(DateQueryError.NOT_A_DATE)
                1 -> {
                    val only = remaining.single()
                    if (only.kind != NumberKind.ORDINAL) return failure(DateQueryError.MISSING_MONTH)
                    month = only.value
                }

                2 -> {
                    val (first, second) = remaining
                    val onlyFirstIsOrdinal = first.kind == NumberKind.ORDINAL && second.kind != NumberKind.ORDINAL
                    if (onlyFirstIsOrdinal) {
                        day = second.value
                        month = first.value
                    } else {
                        day = first.value
                        month = second.value
                    }
                }

                else -> return failure(DateQueryError.AMBIGUOUS)
            }
        }

        val resolvedMonth = month ?: return failure(DateQueryError.MISSING_MONTH)
        if (resolvedMonth !in 1..12) return failure(DateQueryError.MONTH_OUT_OF_RANGE)
        if (day != null && day !in 1..30) return failure(DateQueryError.DAY_OUT_OF_RANGE)
        return ParseDateQueryResult.Success(DateQuery(month = resolvedMonth, day = day))
    }

    // endregion

    private fun failure(error: DateQueryError) = ParseDateQueryResult.Failure(error)

    private class Word(val text: String, val hasGeresh: Boolean)

    private enum class NumberKind { CARDINAL, ORDINAL, GEMATRIA, DIGITS }

    private enum class Role { DAY, MONTH, YEAR }

    private sealed interface Item {
        data class Number(
            val value: Int,
            val kind: NumberKind,
            val prefix: String,
            val isBareShnayim: Boolean = false,
        ) : Item

        data class MonthName(val month: Int) : Item
        data class Chodesh(val prefixedByBetOrLamed: Boolean) : Item
        data class Year(val construct: Boolean) : Item
        data object RoshChodesh : Item
        data object Other : Item
    }

    private sealed interface Lexeme {
        data class Unit(val value: Int) : Lexeme
        data class Ten(val value: Int) : Lexeme
        /** שתי, עשתי: only a number in front of עשר/עשרה (see TEEN_ONLY_UNITS). */
        data object TeenUnit : Lexeme
        data class Ordinal(val value: Int) : Lexeme
        data class Month(val month: Int) : Lexeme
        data class Year(val construct: Boolean) : Lexeme
        data object Chodesh : Lexeme
        data object Rosh : Lexeme
        data object RoshChodesh : Lexeme
        data object Drop : Lexeme
    }

    private companion object {
        const val ADAR = 12
        const val MAX_PREFIX_LENGTH = 3
        const val GERESH = '\''
        const val MAKAF = '־'
        const val VARIATION_SELECTOR = '️'
        const val HUNDREDS = "מאות"

        const val PREFIX_LETTERS = "ובלהמ"
        const val GERESH_CHARS = "׳״'\"`’‘”“"

        /** In the nikud range but separate words: makaf, paseq, sof pasuk, nun hafucha. */
        const val PUNCTUATION_MARKS = "־׀׃׆"

        val SHNAYIM = setOf("שנימ", "שניימ")
        val TEEN_SUFFIXES = setOf("עשר", "עשרה")
        val ADAR_SUFFIXES = setOf("א", "ב", "ראשונ", "שני", "ראשונה", "שניה", "שנייה")

        /** Units that only exist in front of עשר/עשרה: שני עשר, שתי עשרה, עשתי עשר. */
        val TEEN_ONLY_UNITS = mapOf("שני" to 2, "שתי" to 2, "עשתי" to 1)

        fun Char.isAsciiDigit() = this in '0'..'9'

        fun isHebrewLetter(ch: Char?) = ch != null && ch in 'א'..'ת'

        fun String.isBetOrLamed() = 'ב' in this || 'ל' in this

        fun String.withRegularFinals(): String = map {
            when (it) {
                'ך' -> 'כ'
                'ם' -> 'מ'
                'ן' -> 'נ'
                'ף' -> 'פ'
                'ץ' -> 'צ'
                else -> it
            }
        }.joinToString("")

        /** Canonical letters for 1..30: א … ט, י, יא … יד, טו, טז, יז … יט, כ … כט, ל. */
        val GEMATRIA: Map<String, Int> = (1..30).associateBy { DateQuery.dayToLetters(it) }

        val LEXICON: Map<String, Lexeme> = buildMap {
            fun add(value: Lexeme, vararg words: String) = words.forEach { put(it.withRegularFinals(), value) }

            add(Lexeme.Unit(1), "אחד", "אחת")
            add(Lexeme.Unit(2), "שנים", "שניים", "שתים", "שתיים", "תרין", "תרתין")
            add(Lexeme.Unit(3), "שלשה", "שלושה", "שלש", "שלוש", "תלת", "תלתה", "תלתא")
            add(Lexeme.Unit(4), "ארבעה", "ארבע")
            add(Lexeme.Unit(5), "חמשה", "חמישה", "חמש")
            add(Lexeme.Unit(6), "ששה", "שישה", "שש", "שת")
            add(Lexeme.Unit(7), "שבעה", "שבע")
            add(Lexeme.Unit(8), "שמונה", "שמנה", "תמניה", "תמנה")
            add(Lexeme.Unit(9), "תשעה", "תשע")
            add(Lexeme.TeenUnit, "שתי", "עשתי")
            add(Lexeme.Ten(10), "עשרה", "עשר", "עשור")
            add(Lexeme.Ten(20), "עשרים")
            add(Lexeme.Ten(30), "שלשים", "שלושים")
            add(Lexeme.Ten(40), "ארבעים")
            add(Lexeme.Ten(50), "חמשים", "חמישים")
            add(Lexeme.Ten(60), "ששים", "שישים")
            add(Lexeme.Ten(70), "שבעים")
            add(Lexeme.Ten(80), "שמונים", "שמנים")
            add(Lexeme.Ten(90), "תשעים")
            add(Lexeme.Ten(100), "מאה", "מאת")
            add(Lexeme.Ten(200), "מאתים", "מאתיים")

            add(Lexeme.Ordinal(1), "ראשון", "ראשן", "ראשונה")
            add(Lexeme.Ordinal(2), "שני", "שנית", "שניה", "שנייה")
            add(Lexeme.Ordinal(3), "שלישי", "שלשי", "שלישית", "שלשית")
            add(Lexeme.Ordinal(4), "רביעי", "רבעי", "רביעית", "רבעית")
            add(Lexeme.Ordinal(5), "חמישי", "חמשי", "חמישית", "חמשית")
            add(Lexeme.Ordinal(6), "שישי", "ששי", "שישית", "ששית")
            add(Lexeme.Ordinal(7), "שביעי", "שבעי", "שביעית", "שבעית")
            add(Lexeme.Ordinal(8), "שמיני", "שמני", "שמינית")
            add(Lexeme.Ordinal(9), "תשיעי", "תשעי", "תשיעית", "תשעית")
            add(Lexeme.Ordinal(10), "עשירי", "עשרי", "עשירית", "עשרית")

            add(Lexeme.Month(1), "ניסן", "אביב")
            add(Lexeme.Month(2), "אייר", "איר", "זיו", "זו")
            add(Lexeme.Month(3), "סיון", "סיוון")
            add(Lexeme.Month(4), "תמוז")
            add(Lexeme.Month(5), "אב")
            add(Lexeme.Month(6), "אלול")
            add(Lexeme.Month(7), "תשרי", "איתנים", "אתנים")
            add(Lexeme.Month(8), "חשוון", "חשון", "מרחשוון", "מרחשון", "בול")
            add(Lexeme.Month(9), "כסלו", "כסליו")
            add(Lexeme.Month(10), "טבת")
            add(Lexeme.Month(11), "שבט")
            add(Lexeme.Month(ADAR), "אדר")

            add(Lexeme.Chodesh, "חדש", "חודש", "ירח")
            add(Lexeme.Year(construct = false), "שנה")
            add(Lexeme.Year(construct = true), "שנת")
            add(Lexeme.Rosh, "ראש")
            add(Lexeme.RoshChodesh, "רח")
            add(Lexeme.Drop, "יום", "הוא", "היא", "הזה", "הזו", "הזאת", "בו", "די", "מנחם")
        }
    }
}
